/**
 * Authentication API
 *
 * This module provides functions for authentication-related API calls:
 * - register: Create a new company and admin user
 * - login: Authenticate and get JWT token
 * - getMe: Get current authenticated user
 * - inviteUser: Invite a new user to the company
 */

import api from './index';

/**
 * Register a new company and admin user
 *
 * Creates a new company and the first admin user for that company.
 * Returns a JWT token that can be used for subsequent requests.
 *
 * @param {Object} data - Registration data
 * @param {string} data.companyName - Name of the company
 * @param {string} data.name - User's full name
 * @param {string} data.email - User's email address
 * @param {string} data.password - User's password (min 8 characters)
 *
 * @returns {Promise<Object>} Response with token, user, and company data
 *
 * @example
 * const result = await register({
 *   companyName: 'Acme Corp',
 *   name: 'John Doe',
 *   email: 'john@acme.com',
 *   password: 'securepassword123'
 * });
 * // result.data.token - JWT token
 * // result.data.user - User object
 * // result.data.company - Company object
 */
export const register = (data) => {
  return api.post('/auth/register', data);
};

/**
 * Log in an existing user
 *
 * Authenticates the user and returns a JWT token.
 * The token should be stored and sent with subsequent requests.
 *
 * @param {string} email - User's email address
 * @param {string} password - User's password
 *
 * @returns {Promise<Object>} Response with token, user, and company data
 *
 * @example
 * const result = await login('john@acme.com', 'securepassword123');
 * localStorage.setItem('token', result.data.token);
 */
export const login = (email, password) => {
  return api.post('/auth/login', { email, password });
};

/**
 * Get current authenticated user
 *
 * Retrieves the user profile for the currently authenticated user.
 * Requires a valid JWT token in the Authorization header.
 *
 * @returns {Promise<Object>} Response with user and company data
 *
 * @example
 * const result = await getMe();
 * // result.data.user - Current user object
 * // result.data.company - User's company object
 */
export const getMe = () => {
  return api.get('/auth/me');
};

/**
 * Invite a new user to the company
 *
 * Creates a new user account for the current user's company.
 * Only admins can invite new users.
 *
 * @param {Object} data - User data
 * @param {string} data.email - New user's email
 * @param {string} data.name - New user's name
 * @param {string} data.password - New user's initial password
 * @param {string} [data.role='user'] - User's role ('admin' or 'user')
 *
 * @returns {Promise<Object>} Response with new user data
 */
export const inviteUser = (data) => {
  return api.post('/auth/invite', data);
};

/**
 * Get all users in the company (admin only)
 *
 * @returns {Promise<Object>} Response with users array
 */
export const getCompanyUsers = () => {
  return api.get('/auth/users');
};

/**
 * Update company details (admin only)
 *
 * @param {Object} data - Company data
 * @param {string} data.name - New company name
 *
 * @returns {Promise<Object>} Response with updated company data
 */
export const updateCompany = (data) => {
  return api.put('/auth/company', data);
};

/**
 * Update a user in the company (admin only)
 *
 * @param {string} userId - ID of the user to update
 * @param {Object} data - Fields to update (name, email, password, role)
 *
 * @returns {Promise<Object>} Response with updated user data
 */
export const updateUser = (userId, data) => {
  return api.put(`/auth/users/${userId}`, data);
};

/**
 * Delete a user from the company (admin only)
 *
 * @param {string} userId - ID of the user to delete
 *
 * @returns {Promise<Object>} Response with success message
 */
export const deleteUser = (userId) => {
  return api.delete(`/auth/users/${userId}`);
};

/**
 * Upload company logo (admin only)
 *
 * @param {File} file - Image file to upload
 *
 * @returns {Promise<Object>} Response with updated company data including logoUrl
 */
export const uploadCompanyLogo = (file) => {
  const formData = new FormData();
  formData.append('logo', file);

  return api.put('/auth/company/logo', formData, {
    headers: { 'Content-Type': undefined }
  });
};

/**
 * Delete company logo (admin only)
 *
 * @returns {Promise<Object>} Response with updated company data
 */
export const deleteCompanyLogo = () => {
  return api.delete('/auth/company/logo');
};
